import React from 'react'
import { DSButton, DSText, DSSpacer } from '@zoe/ds-web'
import styled from 'styled-components'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { onNextClickType } from '../types'
import { QuizBase } from '../../redux/quiz/types'
import {
  calculateImprovedScore,
  calculateSymptomScore,
  FutureScoreBucket,
  getFutureScoreBucket,
} from '../../libs/symptomScore/utils'
import { AnimatedScoreGraph } from './AnimatedScoreGraph'
import { ContextAwareText } from '../core/text/ContextAwareText'

const HeroImage = styled.img`
  display: block;
  width: 100%;
  border-radius: 16px;
`

export type FutureScoreVariant = {
  title: string
  description: string
  image?: string
}

export type FutureScoreScreenProps = {
  variants: Record<FutureScoreBucket, FutureScoreVariant>
  startDateLabel?: string
  endDateLabel?: string
  buttonText?: string
}

type Props = FutureScoreScreenProps & {
  onNextClick: onNextClickType
  quiz: QuizBase
}

export const FutureScoreScreen: React.FC<Props> = ({
  variants,
  startDateLabel,
  endDateLabel,
  buttonText = 'Continue',
  onNextClick,
  quiz,
}) => {
  const score = calculateSymptomScore(quiz)
  const bucket = getFutureScoreBucket(score)
  const variant = variants[bucket]
  const improvedScore = calculateImprovedScore(score)
  const showImage = bucket === '99-100' && variant.image

  return (
    <>
      <ScreenContainer>
        <DSText variant="fluid-heading-700" weight="semiBold">
          {variant.title}
        </DSText>
        <DSSpacer size={24} direction="vertical" />
        {showImage ? (
          <HeroImage src={variant.image} alt="" />
        ) : (
          <AnimatedScoreGraph
            startValue={score}
            endValue={improvedScore}
            startDateLabel={startDateLabel}
            endDateLabel={endDateLabel}
          />
        )}
        <DSSpacer size={24} direction="vertical" />
        <DSText variant="fluid-paragraph-300" weight="regular">
          <ContextAwareText text={variant.description} quiz={quiz} />
        </DSText>
      </ScreenContainer>
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={() => (onNextClick as () => void)()}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              {buttonText}
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}
